import { PlatformIndividual } from '../../../types/Individual'
import { EthnicDesignation, Religion } from '../../../types/API'
import { EntityTransformer, ValidationResult } from './types'
import { fuzzyMatch } from '../../fuzzyMatch'
import { UserTypeCandidates } from '../index'
import normalizeDateToISO from '../../util/normalizeDateToISO'
import { ValidationPatterns } from '../../validation'

export interface IndividualFormData {
  title?: string
  firstName?: string
  familyName?: string
  preferredName?: string
  email?: string
  role?: string
  jobTitle?: string
  joinDate?: string
  gender?: string
  religion?: string
  ethnicDesignation?: string
  contactNumber?: string
  dateOfBirth?: string
}

export const individualTransformer: EntityTransformer<PlatformIndividual, IndividualFormData> = {
  entityType: 'Individual',
  toEntity (raw: Record<string, unknown>): Partial<PlatformIndividual> {
    const role = raw.role ? fuzzyMatch(String(raw.role), UserTypeCandidates) : undefined

    const religion = raw.religion as Religion | undefined
    const ethnicDesignation = raw.ethnicDesignation as EthnicDesignation | undefined

    return {
      title: raw.title as string | undefined,
      firstName: raw.firstName as string | undefined,
      familyName: raw.familyName as string | undefined,
      preferredName: raw.preferredName as string | undefined,
      email: (raw.email as string | undefined)?.toLowerCase(),
      role: role ?? undefined,
      jobTitle: raw.jobTitle as string | undefined,
      joinDate: normalizeDateToISO(raw.joinDate as string | undefined),
      gender: raw.gender as string | undefined,
      religion,
      ethnicDesignation,
      contactNumber: raw.contactNumber as string | undefined,
      dateOfBirth: normalizeDateToISO(raw.dateOfBirth as string | undefined)
    }
  },
  toFormData (entity: Partial<PlatformIndividual>): IndividualFormData {
    return {
      title: entity.title ?? undefined,
      firstName: entity.firstName ?? undefined,
      familyName: entity.familyName ?? undefined,
      preferredName: entity.preferredName,
      email: entity.email ?? undefined,
      role: entity.role ?? undefined,
      jobTitle: entity.jobTitle ?? undefined,
      joinDate: entity.joinDate ?? undefined,
      gender: entity.gender ?? undefined,
      religion: entity.religion ?? undefined,
      ethnicDesignation: entity.ethnicDesignation ?? undefined,
      contactNumber: entity.contactNumber ?? undefined,
      dateOfBirth: entity.dateOfBirth ?? undefined
    }
  },
  fromFormData (formData: IndividualFormData): Record<string, unknown> {
    return {
      title: formData.title,
      firstName: formData.firstName,
      familyName: formData.familyName,
      preferredName: formData.preferredName,
      email: formData.email,
      role: formData.role,
      jobTitle: formData.jobTitle,
      joinDate: formData.joinDate,
      gender: formData.gender,
      religion: formData.religion,
      ethnicDesignation: formData.ethnicDesignation,
      contactNumber: formData.contactNumber,
      dateOfBirth: formData.dateOfBirth
    }
  },
  validate (entity: Partial<PlatformIndividual>): ValidationResult {
    const missingFields: string[] = []

    if (!entity.preferredName) {
      missingFields.push('Preferred Name')
    }
    if (!entity.email || !ValidationPatterns.email.test(entity.email)) {
      missingFields.push('Email')
    }
    if (!entity.role) {
      missingFields.push('Role')
    }
    if (!entity.joinDate) {
      missingFields.push('Join Date')
    }

    return {
      valid: missingFields.length === 0,
      missingFields
    }
  }
}
